/* eslint-disable @typescript-eslint/no-magic-numbers */
import { HEX_COLOR_PATTERN } from "../constants";
import { IHsl, IRgb } from "../models/color.models";

const TRAIL_POINT_LIGHT_LIGHTNESS_THRESHOLD = 0.5;
const TRAIL_POINT_LIGHTNESS_SHIFT = 0.4;

const clamp = ({
  value,
  min,
  max,
}: {
  value: number;
  min: number;
  max: number;
}): number => Math.min(max, Math.max(min, value));

export const normalizeHex = (raw: string): string => {
  const trimmed = raw.trim();

  if (!trimmed) {
    return "";
  }

  return trimmed.startsWith("#") ? trimmed : `#${trimmed}`;
};

export const isValidHex = (value: string): boolean => {
  return HEX_COLOR_PATTERN.test(value || "");
};

const expandShortHex = (hex: string): string => {
  if (hex.length === 4) {
    return `#${hex[1]}${hex[1]}${hex[2]}${hex[2]}${hex[3]}${hex[3]}`;
  }

  return hex;
};

const parseHexToRgb = (hex: string): IRgb | undefined => {
  const normalized = normalizeHex(hex);
  const expanded = expandShortHex(normalized.toLowerCase());
  const match = /^#([0-9a-f]{6})([0-9a-f]{2})?$/i.exec(expanded);

  if (!match) {
    return undefined;
  }

  const hex6 = match[1];

  return {
    r: parseInt(hex6.slice(0, 2), 16),
    g: parseInt(hex6.slice(2, 4), 16),
    b: parseInt(hex6.slice(4, 6), 16),
  };
};

const parseRgbStringToRgb = (value: string): IRgb | undefined => {
  const match = /^rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)/i.exec(value.trim());

  if (!match) {
    return undefined;
  }

  return {
    r: clamp({ value: Number(match[1]), min: 0, max: 255 }),
    g: clamp({ value: Number(match[2]), min: 0, max: 255 }),
    b: clamp({ value: Number(match[3]), min: 0, max: 255 }),
  };
};

const hueChannelFromHsl = ({
  chromaMin,
  chromaMax,
  hueFraction,
}: {
  chromaMin: number;
  chromaMax: number;
  hueFraction: number;
}): number => {
  let normalizedHue = hueFraction;

  if (normalizedHue < 0) {
    normalizedHue += 1;
  }

  if (normalizedHue > 1) {
    normalizedHue -= 1;
  }

  if (normalizedHue < 1 / 6) {
    return chromaMin + (chromaMax - chromaMin) * 6 * normalizedHue;
  }

  if (normalizedHue < 1 / 2) {
    return chromaMax;
  }

  if (normalizedHue < 2 / 3) {
    return chromaMin + (chromaMax - chromaMin) * (2 / 3 - normalizedHue) * 6;
  }

  return chromaMin;
};

const hslToRgb = ({ h, s, l }: IHsl): IRgb => {
  if (s === 0) {
    const gray = l * 255;

    return { r: gray, g: gray, b: gray };
  }

  const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
  const p = 2 * l - q;
  const hk = h % 1;

  return {
    r:
      hueChannelFromHsl({
        chromaMin: p,
        chromaMax: q,
        hueFraction: hk + 1 / 3,
      }) * 255,
    g:
      hueChannelFromHsl({
        chromaMin: p,
        chromaMax: q,
        hueFraction: hk,
      }) * 255,
    b:
      hueChannelFromHsl({
        chromaMin: p,
        chromaMax: q,
        hueFraction: hk - 1 / 3,
      }) * 255,
  };
};

const parseHslStringToRgb = (value: string): IRgb | undefined => {
  const match = /^hsla?\(\s*([\d.]+)\s*,\s*([\d.]+)%\s*,\s*([\d.]+)%/i.exec(
    value.trim(),
  );

  if (!match) {
    return undefined;
  }

  return hslToRgb({
    h: Number(match[1]) / 360,
    s: Number(match[2]) / 100,
    l: Number(match[3]) / 100,
  });
};

export const parseCssColorToRgb = (color: string): IRgb | undefined => {
  const trimmed = color.trim();

  if (!trimmed) {
    return undefined;
  }

  if (trimmed.startsWith("#")) {
    return parseHexToRgb(trimmed);
  }

  if (trimmed.toLowerCase().startsWith("rgb")) {
    return parseRgbStringToRgb(trimmed);
  }

  if (trimmed.toLowerCase().startsWith("hsl")) {
    return parseHslStringToRgb(trimmed);
  }

  if (/^[0-9a-f]{3}([0-9a-f]{3})?$/i.test(trimmed)) {
    return parseHexToRgb(`#${trimmed}`);
  }

  return undefined;
};

const linearizeChannel = (channel: number): number => {
  const normalized = channel / 255;

  return normalized <= 0.03928
    ? normalized / 12.92
    : ((normalized + 0.055) / 1.055) ** 2.4;
};

export const getRelativeLuminance = (rgb: IRgb): number => {
  const r = linearizeChannel(rgb.r);
  const g = linearizeChannel(rgb.g);
  const b = linearizeChannel(rgb.b);

  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
};

const rgbToHsl = ({ r, g, b }: IRgb): IHsl => {
  const rNorm = r / 255;
  const gNorm = g / 255;
  const bNorm = b / 255;
  const max = Math.max(rNorm, gNorm, bNorm);
  const min = Math.min(rNorm, gNorm, bNorm);
  const l = (max + min) / 2;
  let h = 0;
  let s = 0;

  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);

    switch (max) {
      case rNorm:
        h = ((gNorm - bNorm) / d + (gNorm < bNorm ? 6 : 0)) / 6;
        break;
      case gNorm:
        h = ((bNorm - rNorm) / d + 2) / 6;
        break;
      default:
        h = ((rNorm - gNorm) / d + 4) / 6;
        break;
    }
  }

  return { h, s, l };
};

export const rgbToHex = ({ r, g, b }: IRgb): string => {
  const toHex = (channel: number): string =>
    clamp({ value: Math.round(channel), min: 0, max: 255 })
      .toString(16)
      .padStart(2, "0");

  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
};

/**
 * GPS trail dots on the live map: darker when the route line is light, lighter when it is dark.
 * Keeps hue so dots stay visually tied to the ride color.
 */
export const getContrastingTrailPointColor = (lineColor: string): string => {
  const rgb = parseCssColorToRgb(lineColor);

  if (!rgb) {
    return lineColor;
  }

  const hsl = rgbToHsl(rgb);
  const isLight = hsl.l > TRAIL_POINT_LIGHT_LIGHTNESS_THRESHOLD;
  const adjustedLightness = isLight
    ? clamp({
        value: hsl.l - TRAIL_POINT_LIGHTNESS_SHIFT,
        min: 0.12,
        max: 0.48,
      })
    : clamp({
        value: hsl.l + TRAIL_POINT_LIGHTNESS_SHIFT,
        min: 0.52,
        max: 0.88,
      });

  return rgbToHex(hslToRgb({ ...hsl, l: adjustedLightness }));
};
